// Leads › For review: the leads a check left for a person (needs_review), worked
// through like a queue. Mark verified or Mark failed settles one and the next comes
// up; Undo puts it back. Campaigns email verified leads only, so this is how a lead
// the check couldn't prove gets in, or stays out for good. A lead not checked yet
// isn't here: Verify reads it (the count links to them). Hide folds it away for the
// session. A person's call stands against a run over many leads; Verify again on the
// lead's page replaces it (docs/pages/ops-growth.md › Verification).

import { useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '@/api'
import { errorMessage } from '@/api/client'
import type { Lead, VerifyDecisionStatus } from '@/api/types'
import { Button, Card, CardHeader, InlineAlert } from '@/components/ui'
import { invalidateQueries, useQuery } from '@/lib/query'
import { SafeExternalLink } from './shared'
import { CurrentPlatform } from './verifyShared'

/** Shown at once; a few more are fetched so the next is there the moment one is settled. */
const SHOWN = 3
const FETCHED = 8
const REASONS = 2
const HIDDEN_KEY = 'scorelm.growth.leadReview.hidden'

function readHidden(): boolean {
  try {
    return sessionStorage.getItem(HIDDEN_KEY) === '1'
  } catch {
    return false
  }
}

function writeHidden(hidden: boolean) {
  try {
    if (hidden) sessionStorage.setItem(HIDDEN_KEY, '1')
    else sessionStorage.removeItem(HIDDEN_KEY)
  } catch {
    /* a private window: it folds for this page only */
  }
}

function ReviewItem({ lead, busy, onDecide }: { lead: Lead; busy: boolean; onDecide: (status: VerifyDecisionStatus) => void }) {
  const place = [lead.city, lead.state].filter(Boolean).join(', ')
  const site = lead.home_url ?? lead.website
  const reasons = lead.verify_reasons.slice(0, REASONS)
  return (
    <li className="flex flex-wrap items-start justify-between gap-x-4 gap-y-2 border-t border-line pt-3 first:border-t-0 first:pt-0">
      <div className="grid min-w-0 flex-1 basis-64 gap-1">
        <p className="text-[13.5px] font-semibold text-ink">
          <Link to={`/growth/leads/${lead.id}#checks`} className="break-words hover:text-accent">
            {lead.name}
          </Link>
          {place ? <span className="font-medium text-muted"> · {place}</span> : null}
        </p>
        <div className="text-[13px]">
          <CurrentPlatform facts={lead} linked={lead.linked_platform} text={lead.platform} />
        </div>
        {/* What the check said, in its own words: the reasons it held the lead back. */}
        {reasons.length > 0 ? (
          <ul aria-label={`Why ${lead.name} needs review`} className="grid gap-0.5">
            {reasons.map((r) => (
              <li key={r.text} className="break-words text-[13px] text-muted">
                {r.text}
              </li>
            ))}
          </ul>
        ) : null}
        {site ? (
          <span className="text-[13px]">
            <SafeExternalLink href={site} />
          </span>
        ) : null}
      </div>
      <div className="flex shrink-0 flex-wrap gap-2">
        <Button size="sm" disabled={busy} onClick={() => onDecide('verified')}>
          Mark verified
        </Button>
        <Button size="sm" disabled={busy} onClick={() => onDecide('failed')}>
          Mark failed
        </Button>
      </div>
    </li>
  )
}

export function LeadReview({ onShowAll, onShowUnchecked }: { onShowAll: () => void; onShowUnchecked: () => void }) {
  const { data } = useQuery(['growth', 'leads', 'review'], (signal) => api.growth.leads.review(FETCHED, signal))
  const [hidden, setHidden] = useState(readHidden)
  // Settled here: out of the list at once, before the refetch says so.
  const [settled, setSettled] = useState<ReadonlySet<number>>(new Set())
  const [pending, setPending] = useState<number | null>(null)
  const [last, setLast] = useState<{ lead: Lead; status: 'verified' | 'failed' } | null>(null)
  const [problem, setProblem] = useState<string | null>(null)

  // Quiet while it loads or if it can't: the list below has its own states.
  if (!data) return null
  const waiting = data.leads.filter((l) => !settled.has(l.id))
  const left = Math.max(0, data.total - (data.leads.length - waiting.length))
  if (left === 0 && !last) return null

  const decide = async (lead: Lead, status: VerifyDecisionStatus) => {
    setPending(lead.id)
    setProblem(null)
    try {
      await api.growth.leads.decideVerification(lead.id, status)
      setSettled((prev) => {
        const next = new Set(prev)
        if (status === 'needs_review') next.delete(lead.id)
        else next.add(lead.id)
        return next
      })
      setLast(status === 'needs_review' ? null : { lead, status })
      invalidateQueries('growth')
    } catch (err) {
      setProblem(errorMessage(err))
    } finally {
      setPending(null)
    }
  }
  const toggle = () => {
    writeHidden(!hidden)
    setHidden(!hidden)
  }

  return (
    <Card aria-label="For review">
      <CardHeader
        title="For review"
        description={left > 0 ? `${left} ${left === 1 ? 'lead needs' : 'leads need'} a person's look. Campaigns only email verified leads, so mark each one verified or failed.` : 'Nothing left to review.'}
        actions={
          <Button size="sm" variant="ghost" aria-expanded={!hidden} aria-controls="lead-review" onClick={toggle}>
            {hidden ? 'Show' : 'Hide'}
          </Button>
        }
      />
      {hidden ? null : (
        <div id="lead-review" className="grid gap-3">
          {last ? (
            <p role="status" className="flex flex-wrap items-center gap-x-2 rounded-md border border-line bg-raised px-3 py-1 text-[13px] text-ink">
              <span className="min-w-0 break-words">
                Marked {last.lead.name} {last.status}.
              </span>
              <Button size="sm" variant="ghost" disabled={pending !== null} onClick={() => void decide(last.lead, 'needs_review')}>
                Undo
              </Button>
            </p>
          ) : null}
          {problem ? <InlineAlert tone="bad">{problem}</InlineAlert> : null}
          {waiting.length > 0 ? (
            <ul aria-label="Leads to review" className="grid gap-3">
              {waiting.slice(0, SHOWN).map((lead) => (
                <ReviewItem key={lead.id} lead={lead} busy={pending !== null} onDecide={(status) => void decide(lead, status)} />
              ))}
            </ul>
          ) : null}
          {left > SHOWN || data.unverified > 0 ? (
            <div className="flex flex-wrap items-center gap-x-1 gap-y-1 border-t border-line pt-2 text-xs text-muted">
              {left > SHOWN ? (
                <Button size="sm" variant="ghost" onClick={onShowAll}>
                  List all {left}
                </Button>
              ) : null}
              {data.unverified > 0 ? (
                <Button size="sm" variant="ghost" onClick={onShowUnchecked}>
                  {data.unverified} not checked yet
                </Button>
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </Card>
  )
}
